import React, { useEffect, useRef, useState } from "react";
import { ChatCircleDots, X, PaperPlaneRight, WhatsappLogo } from "@phosphor-icons/react";
import { api, WHATSAPP_NUMBER } from "../lib/api";

const SESSION_KEY = "cg_chat_session";
const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const waUrl = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const WELCOME = {
  role: "assistant",
  content:
    "¡Hola! 🍺 Soy Garaje Bot, tu asistente de Ciudad Garaje. ¿Buscas una cerveza en especial, quieres ver el menú o hacer un pedido? Estoy para ayudarte.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = genId();
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  });
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) setMessages([WELCOME]);
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: session, message: text });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply, whatsapp: data.whatsapp_message },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Ups, no pude responder en este momento. Escríbenos por WhatsApp y te atendemos personalmente 🙌",
          whatsapp: "Hola Ciudad Garaje, necesito ayuda con un pedido",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Abrir chat de atención"
        data-testid="chat-launcher"
        className="fixed bottom-24 right-5 md:bottom-28 md:right-7 z-[61] w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#D97736] text-black flex items-center justify-center shadow-lg shadow-black/40 hover:scale-110 hover:bg-[#E88D50] transition-transform"
        style={{ transitionProperty: "transform, background-color" }}
      >
        {open ? (
          <X size={26} weight="bold" />
        ) : (
          <ChatCircleDots size={30} weight="fill" />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[61] bottom-40 right-5 md:bottom-48 md:right-7 w-[calc(100vw-2.5rem)] max-w-sm h-[68vh] max-h-[520px] flex flex-col bg-[#111] border border-white/10 shadow-2xl shadow-black/60 overflow-hidden animate-[cgChatIn_0.25s_ease]"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0A0A0A]">
            <div className="w-9 h-9 border border-[#D97736] flex items-center justify-center shrink-0">
              <span className="font-display text-lg text-[#D97736] leading-none">CG</span>
            </div>
            <div className="leading-tight flex-1">
              <div className="font-display text-xl tracking-wide">Garaje Bot</div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                <span className="text-[0.65rem] uppercase tracking-widest text-gray-400">En línea</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close"
              className="text-gray-400 hover:text-white p-1"
              aria-label="Cerrar chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto cg-scroll px-4 py-4 space-y-4 bg-[#0d0d0d]" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#D97736] text-black"
                      : "bg-[#1c1c1c] text-gray-100 border border-white/5"
                  }`}
                >
                  {m.content}
                  {m.whatsapp && (
                    <a
                      href={waUrl(m.whatsapp)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="chat-whatsapp-cta"
                      className="mt-3 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold px-3 py-2.5 uppercase tracking-wide text-xs transition-colors"
                    >
                      <WhatsappLogo size={18} weight="fill" /> Comprar por WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-[#1c1c1c] border border-white/5 px-3.5 py-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-[#D97736] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-[#D97736] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-[#D97736] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="border-t border-white/10 bg-[#0A0A0A] p-3 flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Escribe tu mensaje..."
              data-testid="chat-input"
              className="flex-1 resize-none bg-[#161616] border border-white/10 focus:border-[#D97736] outline-none text-sm text-gray-100 px-3 py-2.5 max-h-24 cg-scroll"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="chat-send"
              className="w-11 h-11 shrink-0 bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-40 text-black flex items-center justify-center transition-colors"
              aria-label="Enviar"
            >
              <PaperPlaneRight size={20} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
